import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Scale, FileText, Shield, Archive, BookOpen, Briefcase, Download, Upload, History,
} from 'lucide-react';
import { useLang } from '../../context/LanguageContext';
import { API_BASE as API } from '../../lib/apiBase';
import { toast } from 'sonner';
import LegalReviewPanel from '../dashboard/LegalReviewPanel';
import LegalOpsPanel from '../dashboard/LegalOpsPanel';
import LegalArchivePanel from '../dashboard/LegalArchivePanel';
import { ContractsManagerPanel } from '../dashboard/ContractsManagerPanel';

const tabs = (lang) => [
  { id: 'hub', label: lang === 'es' ? 'Inicio' : 'Home', icon: Scale },
  { id: 'review', label: lang === 'es' ? 'Economía y fiscal' : 'Economy & tax', icon: FileText },
  { id: 'docs', label: lang === 'es' ? 'Documentos oficiales' : 'Official documents', icon: Shield },
  { id: 'ops', label: 'Legal Ops', icon: BookOpen },
  { id: 'contracts', label: lang === 'es' ? 'Contratos' : 'Contracts', icon: Briefcase },
  { id: 'archive', label: lang === 'es' ? 'Archivo aceptaciones' : 'Acceptances', icon: Archive },
  { id: 'files', label: lang === 'es' ? 'Archivos y export' : 'Files & export', icon: Upload },
];

/**
 * Full legal workspace for role legal_advisor (requires backend 2FA session).
 * Spanish (es) is the canonical source for legal text; English fields are shown alongside.
 */
export default function LegalAdvisorHub({ token }) {
  const { lang } = useLang();
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    [token]
  );
  const [active, setActive] = useState('hub');
  const [legalDocs, setLegalDocs] = useState([]);
  const [legalForm, setLegalForm] = useState(null);
  const [versions, setVersions] = useState([]);
  const [sectionsJsonEs, setSectionsJsonEs] = useState('[]');
  const [sectionsJsonEn, setSectionsJsonEn] = useState('[]');
  const [typesJsonEs, setTypesJsonEs] = useState('[]');
  const [typesJsonEn, setTypesJsonEn] = useState('[]');
  const [docEditMode, setDocEditMode] = useState('content'); // 'sections' | 'content'
  const [saving, setSaving] = useState(false);
  const [privFiles, setPrivFiles] = useState([]);
  const [privTitle, setPrivTitle] = useState('');
  const [privNote, setPrivNote] = useState('');
  const [uploading, setUploading] = useState(false);

  const loadDocs = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/legal/documents`, { headers });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setLegalDocs(data.documents || []);
    } catch {
      toast.error(lang === 'es' ? 'No se pudieron cargar documentos' : 'Could not load documents');
    }
  }, [headers, lang]);

  const complianceWatchlist = useMemo(() => {
    // Canonical + monetización + music/IP + onboarding. Same slugs as backend seeds + config
    // (`terms_of_service` = legacy TOS; keep aligned with `platform_terms` or deprecate in DB)
    const slugs = [
      'platform_terms',
      'terms_of_service',
      'privacy_policy',
      'cookie_policy',
      'copyright_content_policy',
      'community_rules',
      'booking_inquire_terms',
      'booking_terms',
      'artist_terms',
      'payout_terms',
      'refund_policy',
      'ai_usage_policy',
      'ip_takedown_policy',
      'artist_onboarding_terms',
      'song_upload_rights',
    ];
    return slugs.map((slug) => {
      const d = legalDocs.find((x) => x.slug === slug);
      const st = (d && d.status) || '';
      const ok = st === 'approved';
      return { slug, version: d?.version || 0, status: st, ok, title: d?.title_es || slug, missing: !d };
    });
  }, [legalDocs]);

  const loadVersions = async (slug) => {
    if (!slug) return;
    try {
      const res = await fetch(`${API}/api/legal/documents/${encodeURIComponent(slug)}/versions?limit=20`, { headers });
      if (res.ok) {
        const d = await res.json();
        setVersions(d.versions || []);
      }
    } catch {
      setVersions([]);
    }
  };

  const selectDoc = (doc) => {
    setLegalForm({ ...doc });
    const hasSec = (doc.sections_es && doc.sections_es.length) || (doc.sections_en && doc.sections_en.length);
    setDocEditMode(hasSec ? 'sections' : 'content');
    if (hasSec) {
      setSectionsJsonEs(JSON.stringify(doc.sections_es || [], null, 2));
      setSectionsJsonEn(JSON.stringify(doc.sections_en || [], null, 2));
    } else {
      setSectionsJsonEs('[]');
      setSectionsJsonEn('[]');
    }
    setTypesJsonEs(JSON.stringify(doc.types_es || [], null, 2));
    setTypesJsonEn(JSON.stringify(doc.types_en || [], null, 2));
    loadVersions(doc.slug);
  };

  useEffect(() => {
    loadDocs();
  }, [loadDocs]);

  const saveLegalDoc = async () => {
    if (!legalForm?.slug) return;
    setSaving(true);
    try {
      const hasTypes = (legalForm.types_es && legalForm.types_es.length) || (legalForm.types_en && legalForm.types_en.length) || (legalForm.slug || '').includes('cookie');
      let body = {
        title_es: legalForm.title_es || '',
        title_en: legalForm.title_en || '',
        content_es: legalForm.content_es || '',
        content_en: legalForm.content_en || '',
        status: legalForm.status || 'draft',
        sections_es: legalForm.sections_es || [],
        sections_en: legalForm.sections_en || [],
      };
      if (docEditMode === 'sections') {
        try {
          body.sections_es = JSON.parse(sectionsJsonEs || '[]');
          body.sections_en = JSON.parse(sectionsJsonEn || '[]');
        } catch {
          toast.error(lang === 'es' ? 'JSON inválido en secciones' : 'Invalid JSON in sections');
          setSaving(false);
          return;
        }
      }
      if (hasTypes) {
        try {
          body.types_es = JSON.parse(typesJsonEs || '[]');
          body.types_en = JSON.parse(typesJsonEn || '[]');
        } catch {
          toast.error(lang === 'es' ? 'JSON inválido (tipos)' : 'Invalid types JSON');
          setSaving(false);
          return;
        }
      } else {
        body.types_es = legalForm.types_es || [];
        body.types_en = legalForm.types_en || [];
      }
      const res = await fetch(`${API}/api/legal/documents/${legalForm.slug}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'save');
      }
      const updated = await res.json();
      setLegalForm(updated);
      loadDocs();
      loadVersions(legalForm.slug);
      toast.success(lang === 'es' ? 'Documento guardado (nueva versión en historial).' : 'Saved (version recorded).');
    } catch {
      toast.error(lang === 'es' ? 'Error al guardar' : 'Save failed');
    }
    setSaving(false);
  };

  const translateTitle = async () => {
    if (!legalForm?.title_es?.trim()) return;
    try {
      const res = await fetch(`${API}/api/contracts/translate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          text: legalForm.title_es,
          source_language: 'Spanish',
          target_language: 'English',
        }),
      });
      const data = await res.json();
      if (data.translated_text) {
        setLegalForm((p) => ({ ...p, title_en: data.translated_text }));
        toast.success(lang === 'es' ? 'Título traducido (revisar).' : 'Title translated (review).');
      }
    } catch {
      toast.error('Translation failed');
    }
  };

  const loadPriv = async () => {
    const res = await fetch(`${API}/api/legal-advisor/privileged`, { headers });
    if (res.ok) {
      const d = await res.json();
      setPrivFiles(d.items || []);
    }
  };

  useEffect(() => {
    if (active === 'files') loadPriv();
  }, [active]);

  const uploadPriv = async (e) => {
    e.preventDefault();
    const input = e.target.querySelector('input[type=file]');
    const file = input?.files?.[0];
    if (!file) {
      toast.error(lang === 'es' ? 'Selecciona un archivo' : 'Choose a file');
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', privTitle);
    fd.append('note', privNote);
    try {
      const res = await fetch(`${API}/api/legal-advisor/privileged/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail);
      }
      setPrivTitle('');
      setPrivNote('');
      if (input) input.value = '';
      toast.success(lang === 'es' ? 'Archivo subido' : 'File uploaded');
      loadPriv();
    } catch (err) {
      toast.error(err.message || (lang === 'es' ? 'Error al subir' : 'Upload failed'));
    }
    setUploading(false);
  };

  const downloadPack = () => {
    fetch(`${API}/api/legal-advisor/export/legal-pack`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.blob();
      })
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        const l = document.createElement('a');
        l.href = u;
        l.download = 'pulse-legal-pack.zip';
        l.click();
        URL.revokeObjectURL(u);
      })
      .catch(() => toast.error(lang === 'es' ? 'Error al exportar' : 'Export failed'));
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-24" data-testid="legal-advisor-hub">
      <div className="flex flex-wrap gap-2 p-1 bg-pulse-elevated/50 rounded-2xl border border-pulse-border">
        {tabs(lang).map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setActive(t.id)}
              className={`flex-1 min-w-[120px] rounded-xl px-3 py-2.5 text-sm font-medium inline-flex items-center justify-center gap-2 transition-colors ${
                active === t.id
                  ? 'bg-pulse-primary text-black'
                  : 'text-pulse-muted hover:text-white hover:bg-white/5'
              }`}
            >
              <Icon size={16} />
              {t.label}
            </button>
          );
        })}
      </div>

      {active === 'hub' && (
        <div className="rounded-2xl border border-cyan-500/20 bg-gradient-to-br from-[#0d2847]/40 to-pulse-surface p-6 space-y-4">
          <h2 className="font-heading font-bold text-xl text-white">
            {lang === 'es' ? 'Centro de trabajo legal' : 'Legal work center'}
          </h2>
          <div className="rounded-xl border border-pulse-border/80 bg-pulse-elevated/30 p-4">
            <p className="text-xs font-semibold text-pulse-muted uppercase tracking-wider mb-2">
              {lang === 'es' ? 'Semáforo de documentos (operación + V2 + reserva)' : 'Key documents — status'}
            </p>
            <ul className="space-y-1.5 text-sm">
              {complianceWatchlist.map((row) => (
                <li key={row.slug} className="flex items-center justify-between gap-2">
                  <span className="text-pulse-muted truncate" title={row.slug}>
                    {row.title}
                  </span>
                  <span
                    className={`shrink-0 text-xs font-mono rounded px-2 py-0.5 ${
                      row.missing
                        ? 'bg-red-500/15 text-red-200'
                        : row.ok
                        ? 'bg-emerald-500/20 text-emerald-200'
                        : 'bg-amber-500/15 text-amber-200'
                    }`}
                  >
                    {row.missing
                      ? (lang === 'es' ? 'sin alta' : 'not set up')
                      : row.ok
                        ? (lang === 'es' ? 'Vigente' : 'In force')
                        : (lang === 'es' ? 'Revisión' : 'Review')}
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-[10px] text-pulse-muted mt-2">
              {lang === 'es'
                ? 'Vigente: texto activo hacia el público (usuarios y modal de aceptación). Revisión: aún en preparación, no ofrecido. Sin entrada en base: aún no creada (semilla o import). Documentación interna del repositorio: docs/LAWYER_COUNSEL_*.'
                : 'In force: active toward end users. Review: not published. Not in database: not created yet. Internal repo docs: docs/LAWYER_COUNSEL_*.'}
            </p>
          </div>
          <p className="text-sm text-pulse-muted leading-relaxed">
            {lang === 'es'
              ? 'Texto jurídico de referencia en español (es); el inglés (en) acompaña para equilibrio. Usa el selector de idioma de la app para el resto de la interfaz.'
              : 'Canonical legal text is Spanish (en); English is provided alongside. Use the app language toggle for the rest of the UI.'}
          </p>
          <ul className="text-sm text-pulse-muted list-disc pl-5 space-y-1">
            <li>{lang === 'es' ? 'Documentos oficiales: edición, versiones, vigencia (revisión o vigente).' : 'Official documents: edit, version history, review vs. in force.'}</li>
            <li>{lang === 'es' ? 'Legal Ops: matriz, reglas y textos de UI (solo lectura del paquete).' : 'Legal Ops: matrix, rules, UI copy pack.'}</li>
            <li>{lang === 'es' ? 'Contratos: plantillas, instancias y traducción asistida (EN).' : 'Contracts: templates, instances, EN assist.'}</li>
            <li>{lang === 'es' ? 'Archivos propios: memorias, PDF, anexos; export ZIP del paquete legal.' : 'Privileged file uploads; ZIP export of legal pack.'}</li>
          </ul>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setActive('docs')}
              className="rounded-xl bg-pulse-primary/90 text-black font-semibold px-4 py-2 text-sm"
            >
              {lang === 'es' ? 'Ir a documentos' : 'Open documents'}
            </button>
            <button
              type="button"
              onClick={downloadPack}
              className="rounded-xl border border-pulse-border px-4 py-2 text-sm text-pulse-muted hover:text-white inline-flex items-center gap-2"
            >
              <Download size={16} />
              {lang === 'es' ? 'Descargar ZIP legal' : 'Download legal ZIP'}
            </button>
          </div>
        </div>
      )}

      {active === 'review' && <LegalReviewPanel advisorActions />}

      {active === 'docs' && (
        <div className="grid gap-6 xl:grid-cols-[280px,1fr]">
          <div className="rounded-2xl border border-pulse-border bg-pulse-surface p-4 space-y-2 max-h-[70vh] overflow-y-auto">
            {legalDocs.map((doc) => (
              <button
                key={doc.slug}
                type="button"
                onClick={() => selectDoc(doc)}
                className={`w-full text-left rounded-xl border px-3 py-2.5 ${
                  legalForm?.slug === doc.slug ? 'border-pulse-primary bg-pulse-primary/10' : 'border-pulse-border bg-pulse-elevated'
                }`}
              >
                <p className="text-sm font-medium">{doc.title_es || doc.slug}</p>
                <p className="text-[10px] text-pulse-muted">
                  {doc.slug} · v{doc.version || 1} · {doc.status || '—'}
                </p>
              </button>
            ))}
          </div>
          {legalForm && (
            <div className="rounded-2xl border border-pulse-border bg-pulse-surface p-5 space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="text-xs text-pulse-muted uppercase tracking-wider">
                    {lang === 'es' ? 'Editar (ES fuente, EN acompañamiento)' : 'Edit (ES source, EN secondary)'}
                  </p>
                  <h3 className="font-heading font-bold text-lg text-white">{legalForm.slug}</h3>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={translateTitle}
                    className="text-xs border border-pulse-border rounded-lg px-2 py-1 text-pulse-muted hover:text-white"
                  >
                    {lang === 'es' ? 'ES→EN título' : 'ES→EN title'}
                  </button>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <label className="block text-xs text-pulse-muted">
                  Título ES (oficial)
                  <input
                    className="mt-1 w-full bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm"
                    value={legalForm.title_es || ''}
                    onChange={(e) => setLegalForm((p) => ({ ...p, title_es: e.target.value }))}
                  />
                </label>
                <label className="block text-xs text-pulse-muted">
                  Title EN
                  <input
                    className="mt-1 w-full bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm"
                    value={legalForm.title_en || ''}
                    onChange={(e) => setLegalForm((p) => ({ ...p, title_en: e.target.value }))}
                  />
                </label>
                <label className="block text-xs text-pulse-muted md:col-span-2">
                  {lang === 'es' ? 'Estado' : 'Status'}
                  <select
                    className="mt-1 w-full max-w-xs bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm"
                    value={legalForm.status || 'draft'}
                    onChange={(e) => setLegalForm((p) => ({ ...p, status: e.target.value }))}
                  >
                    <option value="draft">{lang === 'es' ? 'Revisión (no ofrecido a usuarios finales)' : 'Review (not for end users)'}</option>
                    <option value="approved">{lang === 'es' ? 'En vigor' : 'In force'}</option>
                  </select>
                </label>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setDocEditMode('content')}
                  className={`text-xs rounded-lg px-2 py-1 border ${docEditMode === 'content' ? 'border-cyan-500/50 text-cyan-200' : 'border-pulse-border text-pulse-muted'}`}
                >
                  {lang === 'es' ? 'Texto continuo' : 'Plain content'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSectionsJsonEs(JSON.stringify(legalForm.sections_es || [], null, 2));
                    setSectionsJsonEn(JSON.stringify(legalForm.sections_en || [], null, 2));
                    setDocEditMode('sections');
                  }}
                  className={`text-xs rounded-lg px-2 py-1 border ${docEditMode === 'sections' ? 'border-cyan-500/50 text-cyan-200' : 'border-pulse-border text-pulse-muted'}`}
                >
                  {lang === 'es' ? 'Secciones (JSON)' : 'Sections (JSON)'}
                </button>
              </div>
              {docEditMode === 'sections' ? (
                <>
                  <p className="text-xs text-amber-200/90">
                    {lang === 'es'
                      ? 'Secciones en JSON: cada elemento { heading, content }.'
                      : 'Sections as JSON: { heading, content }.'}
                  </p>
                  <div className="grid lg:grid-cols-2 gap-3">
                    <textarea
                      className="w-full min-h-[240px] font-mono text-xs bg-pulse-elevated border border-pulse-border rounded-xl p-3"
                      value={sectionsJsonEs}
                      onChange={(e) => setSectionsJsonEs(e.target.value)}
                      spellCheck={false}
                    />
                    <textarea
                      className="w-full min-h-[240px] font-mono text-xs bg-pulse-elevated border border-pulse-border rounded-xl p-3"
                      value={sectionsJsonEn}
                      onChange={(e) => setSectionsJsonEn(e.target.value)}
                      spellCheck={false}
                    />
                  </div>
                </>
              ) : (
                <div className="grid lg:grid-cols-2 gap-3">
                  <label className="text-xs text-pulse-muted block">
                    Contenido ES
                    <textarea
                      className="mt-1 w-full min-h-[200px] text-sm bg-pulse-elevated border border-pulse-border rounded-xl p-3"
                      value={legalForm.content_es || ''}
                      onChange={(e) => setLegalForm((p) => ({ ...p, content_es: e.target.value }))}
                    />
                  </label>
                  <label className="text-xs text-pulse-muted block">
                    Content EN
                    <textarea
                      className="mt-1 w-full min-h-[200px] text-sm bg-pulse-elevated border border-pulse-border rounded-xl p-3"
                      value={legalForm.content_en || ''}
                      onChange={(e) => setLegalForm((p) => ({ ...p, content_en: e.target.value }))}
                    />
                  </label>
                </div>
              )}
              {((legalForm.types_es && legalForm.types_es.length) ||
                (legalForm.types_en && legalForm.types_en.length) ||
                (legalForm.slug || '').includes('cookie')) ? (
                <div className="grid lg:grid-cols-2 gap-3">
                  <textarea
                    className="w-full min-h-[120px] font-mono text-xs bg-pulse-elevated border border-pulse-border rounded-xl p-3"
                    value={typesJsonEs}
                    onChange={(e) => setTypesJsonEs(e.target.value)}
                    placeholder="types_es JSON"
                    spellCheck={false}
                  />
                  <textarea
                    className="w-full min-h-[120px] font-mono text-xs bg-pulse-elevated border border-pulse-border rounded-xl p-3"
                    value={typesJsonEn}
                    onChange={(e) => setTypesJsonEn(e.target.value)}
                    placeholder="types_en JSON"
                    spellCheck={false}
                  />
                </div>
              ) : null}

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={saving}
                  onClick={saveLegalDoc}
                  className="rounded-xl bg-pulse-primary text-black font-semibold px-5 py-2.5 text-sm disabled:opacity-50"
                >
                  {saving ? '…' : lang === 'es' ? 'Guardar (nueva versión)' : 'Save (new version)'}
                </button>
              </div>

              {versions.length > 0 && (
                <div className="mt-4 border-t border-pulse-border pt-4">
                  <p className="text-xs text-pulse-muted flex items-center gap-2 mb-2">
                    <History size={14} />
                    {lang === 'es' ? 'Versiones anteriores (metadatos)' : 'Previous versions (metadata)'}
                  </p>
                  <ul className="text-[11px] text-pulse-muted max-h-40 overflow-y-auto space-y-1 font-mono">
                    {versions.map((v) => (
                      <li key={v.id || v.created_at}>
                        {v.created_at} — {v.created_by || '—'}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {active === 'ops' && <LegalOpsPanel />}

      {active === 'contracts' && <ContractsManagerPanel api={API} headers={headers} lang={lang} />}

      {active === 'archive' && <LegalArchivePanel token={token} lang={lang} />}

      {active === 'files' && (
        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl border border-pulse-border bg-pulse-surface p-5">
            <h3 className="font-heading font-bold text-lg mb-2 text-white">
              {lang === 'es' ? 'Archivos confidenciales del asesor' : 'Confidential counsel files'}
            </h3>
            <p className="text-xs text-pulse-muted mb-4">
              {lang === 'es' ? 'PDF, DOC, DOCX, TXT, MD. Máx. 25 MB.' : 'PDF, DOC, DOCX, TXT, MD. Max 25 MB.'}
            </p>
            <form onSubmit={uploadPriv} className="space-y-3">
              <input
                value={privTitle}
                onChange={(e) => setPrivTitle(e.target.value)}
                placeholder={lang === 'es' ? 'Título / referencia' : 'Title / reference'}
                className="w-full bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm"
              />
              <textarea
                value={privNote}
                onChange={(e) => setPrivNote(e.target.value)}
                placeholder={lang === 'es' ? 'Nota interna' : 'Internal note'}
                rows={2}
                className="w-full bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm"
              />
              <input type="file" className="text-sm text-pulse-muted" accept=".pdf,.doc,.docx,.txt,.md" />
              <button
                type="submit"
                disabled={uploading}
                className="rounded-xl bg-pulse-primary text-black font-semibold px-4 py-2 text-sm disabled:opacity-50"
              >
                {uploading ? '…' : lang === 'es' ? 'Subir' : 'Upload'}
              </button>
            </form>
          </div>
          <div className="rounded-2xl border border-pulse-border bg-pulse-surface p-5">
            <h3 className="font-heading font-bold text-lg mb-2">{lang === 'es' ? 'Listado' : 'Files'}</h3>
            <ul className="text-sm space-y-2 max-h-64 overflow-y-auto">
              {privFiles.map((f) => (
                <li key={f.id} className="border-b border-pulse-border pb-2">
                  <p className="font-medium text-white">{f.title || f.original_filename}</p>
                  {f.url && (
                    <a
                      href={f.url?.startsWith('http') ? f.url : `${(API || '').replace(/\/$/, '')}${f.url}`}
                      className="text-cyan-400 text-xs"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {lang === 'es' ? 'Abrir' : 'Open'}
                    </a>
                  )}
                  {f.note && <p className="text-xs text-pulse-muted mt-1">{f.note}</p>}
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={downloadPack}
              className="mt-4 w-full rounded-xl border border-cyan-500/30 text-cyan-300 px-4 py-2 text-sm inline-flex items-center justify-center gap-2"
            >
              <Download size={16} />
              {lang === 'es' ? 'Exportar paquete legal (ZIP)' : 'Export legal pack (ZIP)'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
